/**
 * Shipping Date Picker Web Component
 * Uses Flatpickr for consistent mm/dd/yyyy format across all browsers
 * - Configurable minimum business days buffer
 * - Weekend exclusion (Saturday/Sunday disabled)
 * - Persists to Shopify cart attributes
 */
class ShippingDatePicker extends HTMLElement {
  constructor() {
    super();
    this.minDaysBuffer = parseInt(this.dataset.minDays) || 5;
    this.input = this.querySelector('input');
    this.flatpickrInstance = null;
  }

  connectedCallback() {
    if (this.input && typeof flatpickr !== 'undefined') {
      this.init();
    } else {
      // Wait for flatpickr to load
      this.waitForFlatpickr();
    }
  }

  waitForFlatpickr() {
    const checkInterval = setInterval(() => {
      if (typeof flatpickr !== 'undefined' && this.input) {
        clearInterval(checkInterval);
        this.init();
      }
    }, 100);
    // Stop checking after 5 seconds
    setTimeout(() => clearInterval(checkInterval), 5000);
  }

  init() {
    const minDate = this.getNextBusinessDay(new Date(), this.minDaysBuffer);

    this.flatpickrInstance = flatpickr(this.input, {
      dateFormat: 'm/d/Y',
      minDate: minDate,
      disableMobile: true, // Force flatpickr UI on mobile too
      disable: [
        function(date) {
          // Disable weekends (Saturday = 6, Sunday = 0)
          return (date.getDay() === 0 || date.getDay() === 6);
        }
      ],
      onChange: this.handleDateChange.bind(this),
      onReady: () => {
        this.loadExistingDate();
      }
    });
  }

  /**
   * Calculate the next valid business day after adding X business days
   */
  getNextBusinessDay(date, daysToAdd) {
    let result = new Date(date);
    let addedDays = 0;

    while (addedDays < daysToAdd) {
      result.setDate(result.getDate() + 1);
      if (result.getDay() !== 0 && result.getDay() !== 6) {
        addedDays++;
      }
    }

    while (result.getDay() === 0 || result.getDay() === 6) {
      result.setDate(result.getDate() + 1);
    }

    return result;
  }

  /**
   * Handle date change - save to cart attributes
   */
  async handleDateChange(selectedDates, dateStr) {
    if (!dateStr) return;

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            'Desired Shipping Date': dateStr
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save shipping date');
      }

      this.showSaveConfirmation();

      this.dispatchEvent(new CustomEvent('shipping-date-updated', {
        bubbles: true,
        detail: { date: dateStr }
      }));

    } catch (error) {
      console.error('Error saving shipping date:', error);
    }
  }

  showSaveConfirmation() {
    const confirmation = this.querySelector('.save-confirmation');
    if (confirmation) {
      confirmation.style.display = 'inline';
      setTimeout(() => {
        confirmation.style.display = 'none';
      }, 2000);
    }
  }

  /**
   * Load existing date from cart attributes
   */
  async loadExistingDate() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      if (cart.attributes && cart.attributes['Desired Shipping Date']) {
        const existingDate = cart.attributes['Desired Shipping Date'];
        // Set the value directly - flatpickr will parse mm/dd/yyyy
        if (this.flatpickrInstance) {
          this.flatpickrInstance.setDate(existingDate, false);
        }
      }
    } catch (error) {
      console.error('Error loading existing date:', error);
    }
  }

  disconnectedCallback() {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
    }
  }
}

if (!customElements.get('shipping-date-picker')) {
  customElements.define('shipping-date-picker', ShippingDatePicker);
}
